import React from 'react';
import { connect } from 'react-redux';

import {
  createComponent,
  enums,
  Header,
  Label,
  Layout,
  PasswordField,
  ProgressBar,
  Icon,
  SingleSelect,
  Checkbox,
  Button,
  Input,
} from 'console-kit';

import ActivateUserPanelSelectors from '../redux/selectors';
import ActivateUserPanelActions from '../redux/actions';

import AuthImageLogo from '../../../components/AuthImageLogo';

import RefData from '../../../../../ref-data';
import authStatics from '../../../common/static';
import authEnums from '../../../common/enums';

import { pwdStengthEvaluation, rePasswordValidationHelper } from '../../../common/utils';

const _avatarList = _.map(RefData.Collection.hydrateAll(RefData.Enum.AVATAR.USER), function (avatar) {
  const avatarRef = avatar.id.split('.')[1];
  return {
    text:  avatar.name,
    value: avatarRef,
    icon:  (
      <Icon src={`${App.globalContext.assetURL}c/img/icons/avatar/avatar-${avatarRef}.svg`} size={enums.sizes.X6} />
    ),
  };
});

const ActivateUserPanel = createComponent({
  displayName:  'ActivateUserPanel',
  propType:     {},
  defaultProps: {},
  refs:         'activateUserPanelRef',
  state:        {
    passwordValidation: {
      value: {
        rePasswordCorrect:      true,
        rePasswordErrorMessage: undefined,

        passwordErrorMessage: undefined,
        passwordCorrect:      true,
      },
      updater: 'setPasswordValidation',
    },
  },
  handlers: {
    handlePasswordFieldBlur: (props) => () => {
      const _minPwdLength = props.isSupportUser
        ? authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH
        : authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      props.setPasswordValidation({
        ...props.passwordValidation,
        passwordErrorMessage: isPasswordValid ? '' : 'Invalid Password',
        passwordCorrect:      isPasswordValid,
      });
    },

    handleRePasswordFieldBlur: (props) => () => {
      const _minPwdLength = props.isSupportUser
        ? authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH
        : authEnums.pwdStrengthLimits.MIN_LENGTH;

      const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

      const { rePasswordErrorMessage, rePasswordCorrect } = rePasswordValidationHelper(
        props.password,
        props.repassword,
        isPasswordValid
      );

      props.setPasswordValidation({
        ...props.passwordValidation,
        rePasswordCorrect:      rePasswordCorrect,
        rePasswordErrorMessage: rePasswordErrorMessage,
      });
    },

    handleValueChange:
      (p) =>
        (e, { id, value }) => {
          p.onUpdateField({ fieldId: id, value });
        },

    handleClick:
      (props) =>
        (e, { value }) => {
          e.preventDefault();

          const _minPwdLength = props.isSupportUser
            ? authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH
            : authEnums.pwdStrengthLimits.MIN_LENGTH;

          const { isPasswordValid } = pwdStengthEvaluation(props.password, _minPwdLength);

          const { rePasswordErrorMessage, rePasswordCorrect } = rePasswordValidationHelper(
            props.password,
            props.repassword,
            isPasswordValid
          );

          props.setPasswordValidation({
            rePasswordCorrect:      rePasswordCorrect,
            rePasswordErrorMessage: rePasswordErrorMessage,
            passwordErrorMessage:   isPasswordValid ? '' : 'Invalid Password',
            passwordCorrect:        isPasswordValid,
          });

          props.onActivateUserClicked(props.refs.activateUserPanelRef, isPasswordValid && rePasswordCorrect);
        },
  },
  render(props) {
    const _minPwdLength = props.isSupportUser
      ? authEnums.pwdStrengthLimits.SUPPORT_MIN_LENGTH
      : authEnums.pwdStrengthLimits.MIN_LENGTH;

    const { progressBarLabel, progressBarRank, progressBarColor } = pwdStengthEvaluation(props.password, _minPwdLength);

    return (
      <div className={authStatics.SCREEN_CLASSES_WIDE}>
        <form ref={props.setActivateUserPanelRef} action={authStatics.ACTIVATEUSER_FORM_SUBMIT_URL}>
          {/*user for user selection in the BE activation process*/}
          <input type="hidden" value={props.userId} name="id" />

          {/*as the drop down does not have options or an input we need a way to properly store the value
          for form submission, so keeping in inside a hidden input seemed like the best idea */}
          <input type="hidden" value={`avatar.${props.avatar}`} name="avatar" />

          <Layout>
            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.CENTER}>
                <AuthImageLogo />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Header
                  text="Activate your account"
                  isDividing={false}
                  font={{
                    emphasis: enums.font.emphasis.NORMAL,
                  }}
                  size={enums.sizes.X6}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column textAlign={enums.textAlignments.LEFT}>
                <Label
                  text={`Please choose a password and profile icon. Your password must be at least ${
                    _minPwdLength
                  } characters long. For security reasons, we recommend to use a mixture of letters, numbers and special characters. Only good or strong passwords will be accepted.`}
                  hasPadding={false}
                  display={enums.display.PLAIN}
                  size={enums.sizes.X4}
                  isFluid
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <Input label="" isFluid={true} isDisabled={true} value={props.userEmail} />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <PasswordField
                  label="Password"
                  isFluid={true}
                  hasError={!props.passwordValidation.passwordCorrect}
                  errorMessage={props.passwordValidation.passwordErrorMessage}
                  placeholder="Password"
                  id="password"
                  name="password"
                  isUnderlined={false}
                  onChange={props.handleValueChange}
                  onBlur={props.handlePasswordFieldBlur}
                  value={props.password}
                  revealTooltipTextDisabled={authStatics.pwdToggleTooltip}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <PasswordField
                  label="Confirm Password"
                  hasError={!props.passwordValidation.rePasswordCorrect}
                  errorMessage={props.passwordValidation.rePasswordErrorMessage}
                  isFluid={true}
                  placeholder="Confirm Password"
                  id="repassword"
                  name="repassword"
                  isUnderlined={false}
                  onChange={props.handleValueChange}
                  onBlur={props.handleRePasswordFieldBlur}
                  value={props.repassword}
                  revealTooltipTextDisabled={authStatics.pwdToggleTooltip}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <ProgressBar
                percentComplete={progressBarRank}
                size={enums.sizes.MEDIUM}
                color={progressBarColor}
                label={progressBarLabel}
              />
            </Layout.Row>

            <Layout.Row>
              <Layout.Column>
                <SingleSelect
                  label="Profile icon"
                  value={props.avatar}
                  id="avatar"
                  onChange={props.handleValueChange}
                  options={_avatarList}
                  placeholder="Select profile icon"
                  allowAdditions={false}
                  isDisabled={false}
                  isFluid={true}
                />
              </Layout.Column>
            </Layout.Row>

            <Layout.Row>
              <Button.WithAnimation
                actions={{
                  start:   ActivateUserPanelActions.onActivateUserRequest,
                  success: ActivateUserPanelActions.onActivateUserSuccess,
                  failure: ActivateUserPanelActions.onActivateUserFailure,
                }}
              >
                <Button.Primary
                  text="Let's go"
                  float={enums.positions.RIGHT}
                  size={enums.sizes.X4}
                  onClick={props.handleClick}
                  textAlign={enums.textAlignments.CENTER}
                  color={enums.colors.ACCENT}
                  isSolid
                  isFluid
                />
              </Button.WithAnimation>
            </Layout.Row>
          </Layout>
        </form>
      </div>
    );
  },
});

export default connect(
  (state) => {
    return {
      userEmail:     ActivateUserPanelSelectors.userEmail(state),
      userId:        ActivateUserPanelSelectors.userId(state),
      isSupportUser: ActivateUserPanelSelectors.isSupportUser(state),

      password:   ActivateUserPanelSelectors.password(state),
      repassword: ActivateUserPanelSelectors.repassword(state),
      avatar:     ActivateUserPanelSelectors.avatar(state),
    };
  },
  (dispatch) => ({
    onUpdateField({ fieldId, value }) {
      dispatch(ActivateUserPanelActions.updateField(fieldId, value));
    },

    onActivateUserClicked(formRef, isValid) {
      dispatch(ActivateUserPanelActions.onActivateUserRequest(formRef, isValid));
    },
  })
)(ActivateUserPanel);
